import React, { useEffect, useState } from "react";
import {
    Button,
    Card,
    Container,
    Grid,
    Chip,
} from "@mui/material";
import Iconify from "../../components/iconify/iconify";
import Dialog from "../../components/shared/Dialog";
import { color } from "../../utils/constant";
import DoctorSlotBlockForm from "./DoctorSlotBlockForm";
import List from "../../components/shared/List";
import RHFAutocomplete from "../../components/shared/RHFAutocomplete";
import MaterialUIPickers from "../../components/shared/DesktopDatePicker";
import { format } from "date-fns";
import { useForm } from "react-hook-form";
import useDoctorSlotBlock from "../../data/talons/DoctorSlotBlock/useDoctorSlotBlock";

const COLUMN_LIST = [
    { field: "sr_no", headerName: "sr. no.", flex: 1 },
    { field: "doctor_name", headerName: "Doctor Name", flex: 2 },
    { field: "area_name", headerName: "Location Name", flex: 2 },
    {
        field: "block_date",
        headerName: "Date",
        flex: 2,
        renderCell: (params) => {
            if (!params.row.block_date) return "";
            try {
                return format(new Date(params.row.block_date), "dd-MMM-yyyy");
            } catch (e) {
                return params.row.block_date;
            }
        }
    },
    {
        field: "block_time",
        headerName: "Blocked Time",
        flex: 3,

    },
];

export default function DoctorSlotBlock() {
    const [showDialog, setShowDialog] = useState(false);
    const [editData, setEditData] = useState(null);
    const [rowsPerPage, setRowsPerPage] = useState(15);
    const { control, setValue } = useForm();

    const {
        loading,
        slotBlockList,
        paginationInfo,
        doctorList,
        areaList,
        doctorId,
        areaId,
        blockDate,
        setBlockDate,
        fetchDoctorComboList,
        fetchAreaComboList,
        findDoctorFromLabel,
        findLocationFromLabel,
        fetchSlotBlockList,
        deleteDoctorSlotBlock,
    } = useDoctorSlotBlock();

    useEffect(() => {
        fetchDoctorComboList();
        fetchAreaComboList();
        fetchSlotBlockList({
            current_page: 1,
            row_count: rowsPerPage,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowsPerPage]);

    const handleDateChange = (newDate) => {
        if (newDate) {
            setBlockDate(format(newDate, "yyyy-MM-dd"));
        } else {
            setBlockDate(null);
        }
    };

    const onSubmit = async () => {
        await fetchSlotBlockList({ current_page: 1, row_count: rowsPerPage });
    };

    return (
        <Container
            sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
                mt: 1,
            }}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} sm={3} md={2}>
                    <RHFAutocomplete
                        borderRadius={"12px"}
                        name="doctor_name"
                        label="Doctor Name"
                        freeSolo
                        data={doctorList || []}
                        defaultValue={doctorId}
                        onInputChange={(value) => findDoctorFromLabel(value)}
                        id={doctorList?.map((option) => option.id)}
                        renderOption={(props, option) => (
                            <li {...props} key={option}>
                                {option}
                            </li>
                        )}
                        renderTags={(selected, getTagProps) =>
                            selected.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    label={option}
                                    size="small"
                                    color="info"
                                    variant="soft"
                                />
                            ))
                        }
                        control={control}
                        setValue={setValue}
                    />
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                    <RHFAutocomplete
                        borderRadius={"12px"}
                        name="location_name"
                        label="Location Name"
                        freeSolo
                        data={areaList || []}
                        defaultValue={areaId}
                        onInputChange={(value) => findLocationFromLabel(value)}
                        id={areaList?.map((option) => option.id)}
                        renderOption={(props, option) => (
                            <li {...props} key={option}>
                                {option}
                            </li>
                        )}
                        renderTags={(selected, getTagProps) =>
                            selected.map((option, index) => (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={option}
                                    label={option}
                                    size="small"
                                    color="info"
                                    variant="soft"
                                />
                            ))
                        }
                        control={control}
                        setValue={setValue}
                    />
                </Grid>
                <Grid item xs={12} sm={3} md={2}>
                    <MaterialUIPickers
                        onChange={handleDateChange}
                        id="block_date"
                        label="Date"
                        type="date"
                        value={blockDate ? new Date(blockDate) : null}
                        size={"small"}
                        sx={{ width: "100%" }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid
                    container
                    direction="row"
                    justify="flex-right"
                    item
                    xs={6}
                    sm={3}
                    md={2}
                >
                    <Button
                        variant="contained"
                        onClick={onSubmit}
                        sx={{
                            maxHeight: 40,
                            backgroundColor: color.button,
                            borderRadius: 3,
                            "&:hover": {
                                backgroundColor: color.button,
                            },
                        }}
                    >
                        Get Data
                    </Button>
                </Grid>

                <Grid
                    item
                    xs={6}
                    sm={3}
                    md={2}
                    sx={{ marginLeft: "auto", textAlign: "end" }}
                >
                    <Button
                        sx={{
                            height: 40,
                            borderRadius: 3,
                            backgroundColor: color.button,
                            "&:hover": {
                                backgroundColor: color.button,
                            },
                        }}
                        variant="contained"
                        startIcon={<Iconify icon="mingcute:add-line" />}
                        onClick={() => {
                            setEditData(null);
                            setShowDialog(true);
                        }}
                    >
                        {"New"}
                    </Button>
                    <Dialog
                        handleClose={() => {
                            setShowDialog(false);
                            setEditData(null);
                            fetchSlotBlockList({
                                current_page: 1,
                                row_count: rowsPerPage,
                            });
                        }}
                        open={showDialog}
                        fullWidth={true}
                        maxWidth="md"
                    >
                        <DoctorSlotBlockForm
                            editData={editData}
                            postSubmit={async () => {
                                setShowDialog(false);
                                setEditData(null);
                                await fetchSlotBlockList({
                                    current_page: 1,
                                    row_count: rowsPerPage,
                                });
                            }}
                        />
                    </Dialog>
                </Grid>
            </Grid>
            <Card
                sx={{
                    flexGrow: { md: 1 },
                    display: { md: "flex" },
                    flexDirection: { md: "column" },
                    my: "12px",
                    borderRadius: "12px",
                    boxShadow:
                        "0 0 2px 0 rgba(145, 158, 171, 0.2),0 12px 24px -4px rgba(145, 158, 171, 0.12)",
                }}
            >
                <List
                    loading={loading}
                    data={slotBlockList}
                    paginationData={paginationInfo}
                    columnsData={COLUMN_LIST}
                    getRowId={(row) => row.id}
                    autoHeight={true}
                    pageCount={Number(paginationInfo.total_page)}
                    totalRecords={paginationInfo.total_count}
                    onPageChange={async (page, rowsPerPage) => {
                        setRowsPerPage(rowsPerPage);
                        await fetchSlotBlockList({
                            current_page: page,
                            row_count: rowsPerPage,
                        });
                    }}
                    enableDelete={true}
                    disableDelete={false}
                    enableEdit={true}
                    onEditIconPress={(id) => {
                        const row = slotBlockList.find(r => r.id === id);
                        setEditData(row);
                        setShowDialog(true);
                    }}
                    onDeleteIconpress={async (row) => {
                        if (window.confirm("Are you sure you want to delete this blocked slot?")) {
                            const response = await deleteDoctorSlotBlock({ id: row.id });
                            if (response) {
                                await fetchSlotBlockList({
                                    current_page: 1,
                                    row_count: rowsPerPage,
                                });
                            }
                        }
                    }}
                />
            </Card>
        </Container>
    );
}
